import { isEmpty } from 'ramda'

import { RTKTags, api } from '@/store/api'

import Env from '../../../env/env'
import type { BECrudResponse } from '../BECrudResponse'
import { ESocialCheckStatus } from '../Enterprise/Enterprise.types'
import { EReportDataType } from '../Workspace/Workspace.types'

import type {
  BackgroundCheckPdfRequest,
  BackgroundCheckRequest,
  ISocialMediaPostRequest,
  ISocialMediaPostResponse,
  PersonHistoryCrudBeResponse,
  PersonHistoryResponse,
  ReDownloadPdfResponse,
  getSubjectBasicInformationResponse,
  getSubjectBasicInformationRequest,
  CancelReportRequest,
  CancelReportResponse,
  GenarateAnonymousPdfResponse,
  GenarateAnonymousPdfRequest,
  GetSignedPdfUrlRequest,
  GetSignedPdfUrlResponse,
} from './Person.types'

export const personsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // usePersonHistoryQuery
    personHistory: builder.query<PersonHistoryResponse, void>({
      query: () => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/socials/history`,
        method: 'GET',
      }),
      transformResponse: (response: PersonHistoryCrudBeResponse) => {
        return response.info
      },
      providesTags: [RTKTags.userHistory],
    }),

    // useMakeBackgroundCheckOpenedMutation
    makeBackgroundCheckOpened: builder.mutation<void, BackgroundCheckRequest>({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/socials/pdf-viewed/${request.id}`,
        method: 'PUT',
      }),
    }),

    // useReDownloadPdfMutation
    reDownloadPdf: builder.mutation<
      ReDownloadPdfResponse,
      BackgroundCheckPdfRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/socials/re-download-pdf/${request.id}`,
        method: 'GET',
      }),
    }),

    // useCancelReportMutation
    cancelReport: builder.mutation<CancelReportResponse, CancelReportRequest>({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/subjects/${request.id}/cancel-report`,
        method: 'POST',
      }),
      invalidatesTags: [RTKTags.monitoringCenter, RTKTags.workspaceDetails],
      async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
        try {
          await queryFulfilled
          const state = getState() as {
            api: { queries: Record<string, { endpointName: string; originalArgs: unknown } | undefined> }
          }
          const targetEndpoints = new Set([
            'getAllWorkspaceReports',
            'getWorkspaceScopedReports',
          ])
          Object.values(state.api.queries).forEach((entry) => {
            if (!entry || !targetEndpoints.has(entry.endpointName)) return
            dispatch(
              (api.util.updateQueryData as any)(
                entry.endpointName,
                entry.originalArgs,
                (draft: { items: Array<{ type: string; data: any }> }) => {
                  const target = draft.items?.find((item) => {
                    const itemId =
                      item.type === EReportDataType.FaceCheck
                        ? item.data._id
                        : item.data.id
                    return itemId === id
                  })
                  if (target) {
                    target.data.status = ESocialCheckStatus.Cancelled
                  }
                },
              ),
            )
          })
        } catch {}
      },
    }),

    // useGetSubjectBasicInformationQuery
    // useLazyGetSubjectBasicInformationQuery
    getSubjectBasicInformation: builder.query<
      getSubjectBasicInformationResponse,
      getSubjectBasicInformationRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/socials/subjects/${request.id}`,
        method: 'GET',
      }),
      providesTags: [RTKTags.monitoringCenter],
    }),

    // useLazySocialMediaPostQuery
    socialMediaPost: builder.query<
      ISocialMediaPostResponse,
      ISocialMediaPostRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/socials/subjects/${request.id}/posts`,
        method: 'GET',
        params: {
          page: request.page,
          pageSize: request.pageSize,
          platform: request.plataform,
          filterBy: request.filterBy,
          flag: request.flag,
          socialMediaId: request.socialMediaId,
        },
      }),
      transformResponse: (
        response: BECrudResponse<ISocialMediaPostResponse[]>,
      ) => {
        return response.info[0]
      },
      keepUnusedDataFor: 0,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        // This will serialize the endpoint and query arguments into a unique cache key
        const { id, pageSize, filterBy, plataform, flag } = queryArgs
        return `${endpointName}:${id}:${pageSize}:${plataform}:${filterBy}:${flag}`
      },
      merge: (currentCache, newItems) => {
        const { posts } = newItems

        // if offset is not zero, we have to merge only if newItems has a new page
        // if offset is zero, always return newItems.results (replacing with data from backend)
        if (isEmpty(posts.list)) {
          return currentCache
        } else if (isEmpty(currentCache)) {
          return newItems
        }
        posts.list = currentCache.posts.list.concat(posts.list)

        return newItems
      },
    }),

    // useGenerateAnonymousPdfMutation
    generateAnonymousPdf: builder.mutation<
      GenarateAnonymousPdfResponse,
      GenarateAnonymousPdfRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/socials/generate-anonymous-pdf/${request.id}`,
        method: 'GET',
      }),
      invalidatesTags: [RTKTags.monitoringCenter],
    }),

    // useGetSignedPdfUrlMutation
    getSignedPdfUrl: builder.mutation<
      GetSignedPdfUrlResponse,
      GetSignedPdfUrlRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/socials/signed-url/${request.id}`,
        method: 'GET',
        params: { type: request.type },
      }),
    }),

    // useGenerateExpandedPdfMutation
    generateExpandedPdf: builder.mutation<
      GenarateAnonymousPdfResponse,
      GenarateAnonymousPdfRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/socials/generate-expanded-pdf/${request.id}`,
        method: 'GET',
      }),
      invalidatesTags: [RTKTags.monitoringCenter],
    }),
  }),
})

export const {
  usePersonHistoryQuery,
  useMakeBackgroundCheckOpenedMutation,
  useReDownloadPdfMutation,
  useGetSubjectBasicInformationQuery,
  useLazyGetSubjectBasicInformationQuery,
  useLazySocialMediaPostQuery,
  useCancelReportMutation,
  useGenerateAnonymousPdfMutation,
  useGenerateExpandedPdfMutation,
  useGetSignedPdfUrlMutation,
} = personsApi
